import { useMemo } from "react";
import {
  Clock,
  ChefHat,
  Package,
  Truck,
  CheckCircle2,
  Store,
  UtensilsCrossed,
  Utensils,
} from "lucide-react";
import "./OrderStatusStepper.css";

// Icon mapping (shared across types)
const IconMap = {
  Clock: Clock,
  ChefHat: ChefHat,
  Package: Package,
  Truck: Truck,
  CheckCircle2: CheckCircle2,
  Store: Store,
  UtensilsCrossed: UtensilsCrossed,
  Utensils: Utensils,
};

// Stepper configuration per type
const stepperConfigs = {
  Delivery: {
    steps: [
      { id: 1, label: "Pending", icon: "Clock" },
      { id: 2, label: "Preparing", icon: "ChefHat" },
      { id: 3, label: "Ready for Pickup", icon: "Store" },
      { id: 4, label: "Picked Up", icon: "Package" },
      { id: 5, label: "En Route", icon: "Truck" },
      { id: 6, label: "Delivered", icon: "CheckCircle2" },
    ],
  },
  Collection: {
    steps: [
      { id: 1, label: "Pending", icon: "Clock" },
      { id: 2, label: "Preparing", icon: "ChefHat" },
      { id: 3, label: "Ready for Pickup", icon: "Store" },
      { id: 4, label: "Collected", icon: "CheckCircle2" },
    ],
  },
  DineIn: {
    steps: [
      { id: 1, label: "Pending", icon: "Clock" },
      { id: 2, label: "Preparing", icon: "ChefHat" },
      { id: 3, label: "Ready to Served", icon: "Utensils" },
      { id: 4, label: "Completed", icon: "CheckCircle2" },
    ],
  },
};

const OrderStatusStepper = ({ type = "Delivery", currentStep = 1 }) => {
  const config = stepperConfigs[type] || stepperConfigs.Delivery;
  const steps = config.steps;

  const stepsWithIcons = useMemo(() => {
    return steps.map((step) => ({
      ...step,
      Icon: IconMap[step.icon] || Clock,
    }));
  }, [steps]);

  return (
    <div className="order-status-stepper">
      <div className="order-status-stepper__container">
        {/* Background track */}
        <div className="order-status-stepper__track" />

        {/* Progress line */}
        <div
          className="order-status-stepper__progress"
          style={{
            width: `calc(${
              ((currentStep - 1) / (steps.length - 1)) * 100
            }% - 3.125rem)`,
          }}
        />

        {stepsWithIcons.map((step, index) => {
          const { Icon } = step;
          const isCompleted = currentStep > step.id;
          const isActive = currentStep === step.id;
          const isPending = currentStep < step.id;

          return (
            <div key={step.id} className="order-status-stepper__step">
              <div
                className={[
                  "order-status-stepper__icon-wrapper",
                  isCompleted &&
                    "order-status-stepper__icon-wrapper--completed",
                  isActive && "order-status-stepper__icon-wrapper--active",
                  isPending && "order-status-stepper__icon-wrapper--pending",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                {isActive && <div className="order-status-stepper__ping" />}
                <Icon
                  className={[
                    "order-status-stepper__icon",
                    isActive && "order-status-stepper__icon--active",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                />
              </div>
              <span
                className={[
                  "order-status-stepper__label",
                  isCompleted && "order-status-stepper__label--completed",
                  isActive && "order-status-stepper__label--active",
                  isPending && "order-status-stepper__label--pending",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                {step.label}
              </span>
              {isCompleted && (
                <div className="order-status-stepper__checkmark" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default OrderStatusStepper;
